import {
  ArrowDown,
  ArrowUp,
  CalendarDays,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock3,
  ListTodo,
  LoaderCircle,
  Plus,
  RefreshCw,
  RotateCcw,
  Sparkles,
  Trash2,
  TriangleAlert,
  X,
} from "lucide-react";

import {
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";

import {
  formatTime,
} from "./timezone";

import type {
  PlanningApprovalItem,
  PlanningData,
  PlanningScheduleItem,
  PlanningTask,
  PlanningTimeSlot,
  TaskPriority,
} from "./types";

const PRIORITY_LABELS:
  Record<TaskPriority, string> = {
    low: "Низкий",
    normal: "Обычный",
    high: "Высокий",
    urgent: "Срочный",
  };

const PRIORITY_CLASSES:
  Record<TaskPriority, string> = {
    low: "planning-priority-low",
    normal: "planning-priority-normal",
    high: "planning-priority-high",
    urgent: "planning-priority-urgent",
  };

function formatDuration(
  minutes: number,
): string {
  if (minutes <= 0) {
    return "0 мин";
  }

  const hours = Math.floor(
    minutes / 60,
  );

  const remainingMinutes =
    minutes % 60;

  if (
    hours > 0
    && remainingMinutes > 0
  ) {
    return (
      `${hours} ч `
      + `${remainingMinutes} мин`
    );
  }

  if (hours > 0) {
    return `${hours} ч`;
  }

  return `${remainingMinutes} мин`;
}

function formatPlanDate(
  date: string,
): string {
  const value = new Date(
    `${date}T12:00:00+04:00`,
  );

  const formatted =
    new Intl.DateTimeFormat(
      "ru-RU",
      {
        timeZone: "Europe/Samara",
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      },
    ).format(value);

  return (
    formatted.charAt(0).toUpperCase()
    + formatted.slice(1)
  );
}

function shiftDate(
  date: string,
  days: number,
): string {
  const value = new Date(
    `${date}T12:00:00Z`,
  );

  value.setUTCDate(
    value.getUTCDate() + days,
  );

  return value
    .toISOString()
    .slice(0, 10);
}

function getTodayInSamara(): string {
  const parts =
    new Intl.DateTimeFormat(
      "en-CA",
      {
        timeZone: "Europe/Samara",

        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      },
    ).formatToParts(
      new Date(),
    );

  const year = parts.find(
    (part) => part.type === "year",
  )?.value;

  const month = parts.find(
    (part) => part.type === "month",
  )?.value;

  const day = parts.find(
    (part) => part.type === "day",
  )?.value;

  return `${year}-${month}-${day}`;
}

function taskProjectName(
  task: PlanningTask,
): string {
  return (
    task.project?.name
    ?? "Без проекта"
  );
}

function samaraDateTimeToIso(
  date: string,
  time: string,
): string {
  return new Date(
    `${date}T${time}:00+04:00`,
  ).toISOString();
}

function getSamaraTimeInput(
  value: string,
): string {
  return new Intl.DateTimeFormat(
    "en-GB",
    {
      timeZone: "Europe/Samara",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    },
  ).format(
    new Date(value),
  );
}

function addMinutes(
  value: string,
  minutes: number,
): string {
  return new Date(
    new Date(value).getTime()
    + minutes * 60_000,
  ).toISOString();
}

function buildAnnotatedSchedule(
  items: PlanningScheduleItem[],
  date: string,
  workdayStart: string,
  workdayEnd: string,
): PlanningScheduleItem[] {
  const startBoundary =
    new Date(
      samaraDateTimeToIso(
        date,
        workdayStart,
      ),
    ).getTime();

  const endBoundary =
    new Date(
      samaraDateTimeToIso(
        date,
        workdayEnd,
      ),
    ).getTime();

  const sortedItems = [
    ...items,
  ].sort(
    (left, right) =>
      new Date(left.startAt).getTime()
      - new Date(right.startAt).getTime(),
  );

  let latestEnd =
    startBoundary;

  return sortedItems.map(
    (item) => {
      const start =
        new Date(
          item.startAt,
        ).getTime();

      const normalizedEndAt =
        addMinutes(
          item.startAt,
          item.durationMinutes,
        );

      const end =
        new Date(
          normalizedEndAt,
        ).getTime();

      const hasConflict =
        start < latestEnd;

      const outsideWorkday = (
        start < startBoundary
        || end > endBoundary
      );

      latestEnd = Math.max(
        latestEnd,
        end,
      );

      return {
        ...item,
        endAt: normalizedEndAt,
        hasConflict,
        outsideWorkday,
      };
    },
  );
}

function buildFreeSlots(
  items: PlanningScheduleItem[],
  date: string,
  workdayStart: string,
  workdayEnd: string,
): PlanningTimeSlot[] {
  const startBoundary =
    new Date(
      samaraDateTimeToIso(
        date,
        workdayStart,
      ),
    ).getTime();

  const endBoundary =
    new Date(
      samaraDateTimeToIso(
        date,
        workdayEnd,
      ),
    ).getTime();

  const occupied = items
    .map((item) => ({
      start: Math.max(
        startBoundary,
        new Date(
          item.startAt,
        ).getTime(),
      ),

      end: Math.min(
        endBoundary,
        new Date(
          item.endAt,
        ).getTime(),
      ),
    }))
    .filter(
      (interval) =>
        interval.end > interval.start,
    )
    .sort(
      (left, right) =>
        left.start - right.start,
    );

  const merged: Array<{
    start: number;
    end: number;
  }> = [];

  for (const interval of occupied) {
    const previous =
      merged[merged.length - 1];

    if (
      !previous
      || interval.start > previous.end
    ) {
      merged.push({
        ...interval,
      });

      continue;
    }

    previous.end = Math.max(
      previous.end,
      interval.end,
    );
  }

  const slots:
    PlanningTimeSlot[] = [];

  let cursor =
    startBoundary;

  for (const interval of merged) {
    if (interval.start > cursor) {
      slots.push({
        startAt:
          new Date(cursor)
            .toISOString(),

        endAt:
          new Date(interval.start)
            .toISOString(),

        durationMinutes:
          Math.round(
            (
              interval.start
              - cursor
            ) / 60_000,
          ),
      });
    }

    cursor = Math.max(
      cursor,
      interval.end,
    );
  }

  if (cursor < endBoundary) {
    slots.push({
      startAt:
        new Date(cursor)
          .toISOString(),

      endAt:
        new Date(endBoundary)
          .toISOString(),

      durationMinutes:
        Math.round(
          (
            endBoundary
            - cursor
          ) / 60_000,
        ),
    });
  }

  return slots;
}

function uniqueTasks(
  tasks: PlanningTask[],
): PlanningTask[] {
  const tasksById =
    new Map<number, PlanningTask>();

  for (const task of tasks) {
    tasksById.set(
      task.id,
      task,
    );
  }

  return [
    ...tasksById.values(),
  ];
}

function scheduleSignature(
  items: PlanningScheduleItem[],
): string {
  return [...items]
    .sort(
      (left, right) =>
        left.task.id
        - right.task.id,
    )
    .map((item) => [
      item.task.id,
      item.startAt,
      item.durationMinutes,
    ].join(":"))
    .join("|");
}

function ScheduleCard({
  item,
  index,
  total,
  isLocked,
  onTimeChange,
  onDurationChange,
  onMove,
  onRemove,
}: {
  item: PlanningScheduleItem;
  index: number;
  total: number;
  isLocked: boolean;

  onTimeChange:
    (time: string) => void;

  onDurationChange:
    (durationMinutes: number) => void;

  onMove:
    (direction: -1 | 1) => void;

  onRemove:
    () => void;
}) {
  return (
    <article
      className={[
        "planning-schedule-card",

        item.source === "suggested"
          ? "planning-schedule-suggested"
          : "",

        item.hasConflict
          ? "planning-schedule-conflict"
          : "",

        item.outsideWorkday
          ? "planning-schedule-outside"
          : "",
      ].join(" ")}
    >
      <div className="planning-schedule-time">
        <strong>
          {formatTime(item.startAt)}
        </strong>

        <span>
          {formatTime(item.endAt)}
        </span>
      </div>

      <span
        className="planning-schedule-line"
        style={{
          backgroundColor:
            item.task.project?.color
            ?? "#94A3B8",
        }}
      />

      <div className="planning-schedule-copy">
        <div className="planning-task-heading">
          <span>
            {taskProjectName(item.task)}
          </span>

          {item.source === "suggested" && (
            <small>
              <Sparkles size={12} />
              Рекомендация
            </small>
          )}
        </div>

        <h4>{item.task.title}</h4>

        <div className="planning-task-meta">
          <span
            className={[
              "planning-priority",

              PRIORITY_CLASSES[
                item.task.priority
              ],
            ].join(" ")}
          >
            {
              PRIORITY_LABELS[
                item.task.priority
              ]
            }
          </span>

          <span>
            <Clock3 size={13} />

            {formatDuration(
              item.durationMinutes,
            )}
          </span>

          {item.task.isOverdue && (
            <span className="planning-overdue">
              <TriangleAlert size={13} />
              Просрочена
            </span>
          )}
        </div>

        {!isLocked && (
          <div className="planning-editor-row">
            <label className="planning-inline-field">
              <span>Начало</span>

              <input
                type="time"
                step={900}
                value={
                  getSamaraTimeInput(
                    item.startAt,
                  )
                }
                onChange={(event) => {
                  onTimeChange(
                    event.target.value,
                  );
                }}
              />
            </label>

            <label className="planning-inline-field">
              <span>Длительность</span>

              <div className="planning-duration-input">
                <input
                  type="number"
                  min={15}
                  max={720}
                  step={15}
                  value={
                    item.durationMinutes
                  }
                  onChange={(event) => {
                    const parsed =
                      Number.parseInt(
                        event.target.value,
                        10,
                      );

                    if (
                      Number.isInteger(parsed)
                    ) {
                      onDurationChange(
                        Math.min(
                          720,
                          Math.max(
                            15,
                            parsed,
                          ),
                        ),
                      );
                    }
                  }}
                />

                <span>мин</span>
              </div>
            </label>

            <div className="planning-card-actions">
              <button
                type="button"
                onClick={() => {
                  onMove(-1);
                }}
                disabled={index === 0}
                aria-label="Переместить выше"
                title="Переместить выше"
              >
                <ArrowUp size={15} />
              </button>

              <button
                type="button"
                onClick={() => {
                  onMove(1);
                }}
                disabled={
                  index === total - 1
                }
                aria-label="Переместить ниже"
                title="Переместить ниже"
              >
                <ArrowDown size={15} />
              </button>

              <button
                type="button"
                className="planning-remove-action"
                onClick={onRemove}
                aria-label="Исключить из плана"
                title="Исключить из плана"
              >
                <Trash2 size={15} />
              </button>
            </div>
          </div>
        )}

        {(item.hasConflict
          || item.outsideWorkday) && (
          <div className="planning-card-warning">
            <TriangleAlert size={14} />

            <span>
              {item.outsideWorkday
                ? "Задача выходит за рабочее окно 09:00–20:00."
                : "Время пересекается с другой задачей."}
            </span>
          </div>
        )}
      </div>
    </article>
  );
}

function BacklogCard({
  task,
  isLocked,
  onAdd,
}: {
  task: PlanningTask;
  isLocked: boolean;
  onAdd: () => void;
}) {
  return (
    <article className="planning-backlog-card">
      <span
        className="planning-backlog-color"
        style={{
          backgroundColor:
            task.project?.color
            ?? "#94A3B8",
        }}
      />

      <div>
        <span>
          {taskProjectName(task)}
        </span>

        <strong>{task.title}</strong>

        <small>
          <Clock3 size={12} />

          {formatDuration(
            task.plannedDurationMinutes
            ?? 60,
          )}
        </small>
      </div>

      {isLocked ? (
        <span
          className={[
            "planning-priority",

            PRIORITY_CLASSES[
              task.priority
            ],
          ].join(" ")}
        >
          {PRIORITY_LABELS[task.priority]}
        </span>
      ) : (
        <button
          type="button"
          className="planning-add-task-button"
          onClick={onAdd}
          title="Добавить в план"
          aria-label="Добавить в план"
        >
          <Plus size={16} />
        </button>
      )}
    </article>
  );
}

function PlanningPanel({
  data,
  selectedDate,
  isLoading,
  isSubmitting,
  error,
  onDateChange,
  onRefresh,
  onApprove,
  onReopen,
  onClose,
}: {
  data: PlanningData | null;

  selectedDate: string;

  isLoading: boolean;
  isSubmitting: boolean;

  error: string | null;

  onDateChange:
    (date: string) => void;

  onRefresh:
    () => Promise<void>;

  onApprove:
    (
      items: PlanningApprovalItem[],
    ) => Promise<void>;

  onReopen:
    () => Promise<void>;

  onClose:
    () => void;
}) {
  const [
    draftItems,
    setDraftItems,
  ] = useState<
    PlanningScheduleItem[]
  >([]);

  const [
    localError,
    setLocalError,
  ] = useState<string | null>(null);

  useEffect(() => {
    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    function handleKeyDown(
      event: KeyboardEvent,
    ): void {
      if (event.key === "Escape") {
        onClose();
      }
    }

    window.addEventListener(
      "keydown",
      handleKeyDown,
    );

    return () => {
      document.body.style.overflow =
        previousOverflow;

      window.removeEventListener(
        "keydown",
        handleKeyDown,
      );
    };
  }, [onClose]);

  useEffect(() => {
    if (!data) {
      setDraftItems([]);
      setLocalError(null);
      return;
    }

    const sourceItems = (
      data.plan.status === "approved"
        ? data.scheduled
        : data.suggestedSchedule
    );

    setDraftItems(
      sourceItems.map(
        (item) => ({
          ...item,
        }),
      ),
    );

    setLocalError(null);
  }, [
    data,
    selectedDate,
  ]);

  const isLocked =
    data?.plan.status === "approved";

  const annotatedDraft =
    useMemo(
      () => {
        if (!data) {
          return [];
        }

        return buildAnnotatedSchedule(
          draftItems,
          selectedDate,
          data.workday.start,
          data.workday.end,
        );
      },
      [
        data,
        draftItems,
        selectedDate,
      ],
    );

  const freeSlots =
    useMemo(
      () => {
        if (!data) {
          return [];
        }

        return buildFreeSlots(
          annotatedDraft,
          selectedDate,
          data.workday.start,
          data.workday.end,
        );
      },
      [
        annotatedDraft,
        data,
        selectedDate,
      ],
    );

  const taskPool =
    useMemo(
      () => {
        if (!data) {
          return [];
        }

        return uniqueTasks([
          ...data.backlog,

          ...data.scheduled.map(
            (item) => item.task,
          ),

          ...data.suggestions.map(
            (item) => item.task,
          ),
        ]);
      },
      [data],
    );

  const plannedTaskIds =
    useMemo(
      () => new Set(
        annotatedDraft.map(
          (item) => item.task.id,
        ),
      ),
      [annotatedDraft],
    );

  const availableTasks =
    useMemo(
      () => taskPool.filter(
        (task) =>
          !plannedTaskIds.has(
            task.id,
          ),
      ),
      [
        plannedTaskIds,
        taskPool,
      ],
    );

  const conflictCount =
    useMemo(
      () => annotatedDraft.filter(
        (item) =>
          item.hasConflict
          || item.outsideWorkday,
      ).length,
      [annotatedDraft],
    );

  const plannedMinutes =
    useMemo(
      () => annotatedDraft.reduce(
        (total, item) =>
          total + item.durationMinutes,
        0,
      ),
      [annotatedDraft],
    );

  const freeMinutes =
    useMemo(
      () => freeSlots.reduce(
        (total, slot) =>
          total + slot.durationMinutes,
        0,
      ),
      [freeSlots],
    );

  const approvalItems =
    useMemo<PlanningApprovalItem[]>(
      () => annotatedDraft.map(
        (item) => ({
          taskId: item.task.id,

          plannedStartAt:
            item.startAt,

          plannedDurationMinutes:
            item.durationMinutes,
        }),
      ),
      [annotatedDraft],
    );

  const recommendedSignature =
    useMemo(
      () => scheduleSignature(
        data?.suggestedSchedule
        ?? [],
      ),
      [data],
    );

  const currentSignature =
    useMemo(
      () => scheduleSignature(
        draftItems,
      ),
      [draftItems],
    );

  const isDraftChanged = (
    data?.plan.status === "draft"
    && currentSignature
      !== recommendedSignature
  );

  const isToday =
    selectedDate
    === getTodayInSamara();

  const canApprove = (
    data !== null
    && data.plan.status === "draft"
    && conflictCount === 0
    && approvalItems.length > 0
    && !isSubmitting
  );

  function handleDateInput(
    event:
      ChangeEvent<HTMLInputElement>,
  ): void {
    onDateChange(event.target.value);
  }

  function updateDraftItem(
    taskId: number,
    updater:
      (
        item: PlanningScheduleItem,
      ) => PlanningScheduleItem,
  ): void {
    setDraftItems(
      (currentItems) =>
        currentItems.map(
          (item) =>
            item.task.id === taskId
              ? updater(item)
              : item,
        ),
    );

    setLocalError(null);
  }

  function changeTaskTime(
    taskId: number,
    time: string,
  ): void {
    if (
      !/^\d{2}:\d{2}$/.test(time)
    ) {
      return;
    }

    updateDraftItem(
      taskId,
      (item) => ({
        ...item,

        startAt:
          samaraDateTimeToIso(
            selectedDate,
            time,
          ),
      }),
    );
  }

  function changeTaskDuration(
    taskId: number,
    durationMinutes: number,
  ): void {
    updateDraftItem(
      taskId,
      (item) => ({
        ...item,
        durationMinutes,
      }),
    );
  }

  function moveTask(
    taskId: number,
    direction: -1 | 1,
  ): void {
    setDraftItems(
      (currentItems) => {
        const sortedItems =
          buildAnnotatedSchedule(
            currentItems,
            selectedDate,
            data?.workday.start
              ?? "09:00",
            data?.workday.end
              ?? "20:00",
          );

        const currentIndex =
          sortedItems.findIndex(
            (item) =>
              item.task.id === taskId,
          );

        const targetIndex =
          currentIndex + direction;

        if (
          currentIndex < 0
          || targetIndex < 0
          || targetIndex
            >= sortedItems.length
        ) {
          return currentItems;
        }

        const currentItem =
          sortedItems[currentIndex];

        const targetItem =
          sortedItems[targetIndex];

        if (
          !currentItem
          || !targetItem
        ) {
          return currentItems;
        }

        const currentStart =
          currentItem.startAt;

        const targetStart =
          targetItem.startAt;

        return currentItems.map(
          (item) => {
            if (
              item.task.id
              === currentItem.task.id
            ) {
              return {
                ...item,
                startAt: targetStart,
              };
            }

            if (
              item.task.id
              === targetItem.task.id
            ) {
              return {
                ...item,
                startAt: currentStart,
              };
            }

            return item;
          },
        );
      },
    );

    setLocalError(null);
  }

  function removeTask(
    taskId: number,
  ): void {
    setDraftItems(
      (currentItems) =>
        currentItems.filter(
          (item) =>
            item.task.id !== taskId,
        ),
    );

    setLocalError(null);
  }

  function addTask(
    task: PlanningTask,
  ): void {
    if (!data) {
      return;
    }

    const durationMinutes = (
      task.plannedDurationMinutes
      ?? 60
    );

    const slot =
      freeSlots.find(
        (candidate) =>
          candidate.durationMinutes
            >= durationMinutes,
      );

    if (!slot) {
      setLocalError(
        "Для этой задачи нет достаточно длинного "
        + "свободного интервала в рабочем дне.",
      );

      return;
    }

    setDraftItems(
      (currentItems) => [
        ...currentItems,

        {
          task,

          startAt:
            slot.startAt,

          endAt:
            addMinutes(
              slot.startAt,
              durationMinutes,
            ),

          durationMinutes,

          source: "suggested",

          hasConflict: false,
          outsideWorkday: false,
        },
      ],
    );

    setLocalError(null);
  }

  function resetDraft(): void {
    if (!data) {
      return;
    }

    setDraftItems(
      data.suggestedSchedule.map(
        (item) => ({
          ...item,
        }),
      ),
    );

    setLocalError(null);
  }

  const visibleError =
    localError ?? error;

  return (
    <div className="planning-panel-backdrop">
      <section
        className="planning-panel"
        role="dialog"
        aria-modal="true"
        aria-label="Планирование рабочего дня"
      >
        <header className="planning-panel-header">
          <div className="planning-panel-icon">
            <CalendarDays size={24} />
          </div>

          <div className="planning-panel-heading">
            <span>Рабочее пространство</span>

            <h2>Планирование</h2>

            <p>
              Рабочий день 09:00–20:00
              по Самарскому времени
            </p>
          </div>

          <div className="planning-header-actions">
            <button
              type="button"
              className="planning-refresh-button"
              onClick={() => {
                void onRefresh();
              }}
              disabled={
                isLoading
                || isSubmitting
              }
            >
              <RefreshCw
                size={18}
                className={
                  isLoading
                    ? "rotating"
                    : ""
                }
              />

              <span>Обновить</span>
            </button>

            <button
              type="button"
              className="planning-close-button"
              onClick={onClose}
              aria-label="Закрыть планирование"
            >
              <X size={21} />
            </button>
          </div>
        </header>

        <div className="planning-date-toolbar">
          <button
            type="button"
            onClick={() => {
              onDateChange(
                shiftDate(
                  selectedDate,
                  -1,
                ),
              );
            }}
            aria-label="Предыдущий день"
          >
            <ChevronLeft size={19} />
          </button>

          <div className="planning-date-copy">
            <span>
              {formatPlanDate(
                selectedDate,
              )}
            </span>

            <small>
              {data?.timeZoneLabel
                ?? "Самара · UTC+4"}
            </small>
          </div>

          <input
            type="date"
            value={selectedDate}
            onChange={handleDateInput}
            aria-label="Дата планирования"
          />

          <button
            type="button"
            className={
              isToday
                ? "planning-today-active"
                : ""
            }
            onClick={() => {
              onDateChange(
                getTodayInSamara(),
              );
            }}
          >
            Сегодня
          </button>

          <button
            type="button"
            onClick={() => {
              onDateChange(
                shiftDate(
                  selectedDate,
                  1,
                ),
              );
            }}
            aria-label="Следующий день"
          >
            <ChevronRight size={19} />
          </button>
        </div>

        {visibleError && (
          <div className="planning-panel-error">
            <TriangleAlert size={18} />
            <span>{visibleError}</span>
          </div>
        )}

        {isLoading && !data ? (
          <div className="planning-panel-loading">
            <LoaderCircle
              size={31}
              className="rotating"
            />

            <strong>
              Формируем план дня
            </strong>

            <span>
              Проверяем задачи,
              интервалы и приоритеты…
            </span>
          </div>
        ) : data ? (
          <>
            <section className="planning-summary-grid">
              <article>
                <CalendarDays size={20} />

                <div>
                  <span>В расписании</span>

                  <strong>
                    {annotatedDraft.length}
                  </strong>
                </div>
              </article>

              <article>
                <ListTodo size={20} />

                <div>
                  <span>Без времени</span>

                  <strong>
                    {availableTasks.length}
                  </strong>
                </div>
              </article>

              <article>
                <Clock3 size={20} />

                <div>
                  <span>Свободно</span>

                  <strong>
                    {formatDuration(
                      freeMinutes,
                    )}
                  </strong>
                </div>
              </article>

              <article
                className={
                  conflictCount > 0
                    ? "planning-summary-danger"
                    : ""
                }
              >
                <TriangleAlert size={20} />

                <div>
                  <span>Конфликты</span>

                  <strong>
                    {conflictCount}
                  </strong>
                </div>
              </article>
            </section>

            <section
              className={[
                "planning-status-banner",

                data.plan.status
                  === "approved"
                  ? "planning-status-approved"
                  : "planning-status-draft",
              ].join(" ")}
            >
              <div>
                {data.plan.status
                  === "approved" ? (
                  <CheckCircle2 size={21} />
                ) : (
                  <Sparkles size={21} />
                )}

                <div>
                  <strong>
                    {data.plan.status
                      === "approved"
                      ? "План подтверждён"
                      : isDraftChanged
                        ? "Черновик изменён"
                        : "Черновик плана"}
                  </strong>

                  <span>
                    {data.plan.status
                      === "approved"
                      ? `${data.plan.itemCount} задач сохранено в плане`
                      : conflictCount > 0
                        ? "Исправь конфликты перед подтверждением"
                        : `${formatDuration(plannedMinutes)} запланировано`}
                  </span>
                </div>
              </div>

              <div className="planning-status-actions">
                {data.plan.status
                  === "draft"
                  && isDraftChanged && (
                  <button
                    type="button"
                    className="planning-reset-button"
                    onClick={resetDraft}
                    disabled={isSubmitting}
                  >
                    <RotateCcw size={16} />
                    Сбросить
                  </button>
                )}

                {data.plan.status
                  === "approved" ? (
                  <button
                    type="button"
                    onClick={() => {
                      void onReopen();
                    }}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <LoaderCircle
                        size={16}
                        className="rotating"
                      />
                    ) : (
                      <RefreshCw size={16} />
                    )}

                    Вернуть в черновик
                  </button>
                ) : (
                  <button
                    type="button"
                    className="planning-approve-button"
                    onClick={() => {
                      void onApprove(
                        approvalItems,
                      );
                    }}
                    disabled={!canApprove}
                  >
                    {isSubmitting ? (
                      <LoaderCircle
                        size={16}
                        className="rotating"
                      />
                    ) : (
                      <CheckCircle2
                        size={16}
                      />
                    )}

                    Подтвердить план
                  </button>
                )}
              </div>
            </section>

            {data.plan.status === "draft" && (
              <div className="planning-local-note">
                <Sparkles size={15} />

                <span>
                  Изменения сохранятся после нажатия
                  «Подтвердить план».
                </span>
              </div>
            )}

            <div className="planning-workspace">
              <main className="planning-schedule-column">
                <div className="planning-section-heading">
                  <div>
                    <CalendarDays size={19} />

                    <div>
                      <h3>Расписание дня</h3>

                      <span>
                        {annotatedDraft.length}
                        {" задач · "}
                        {data.workday.start}
                        {"–"}
                        {data.workday.end}
                      </span>
                    </div>
                  </div>
                </div>

                {annotatedDraft.length > 0 ? (
                  <div className="planning-schedule-list">
                    {annotatedDraft.map(
                      (item, index) => (
                        <ScheduleCard
                          key={item.task.id}
                          item={item}
                          index={index}
                          total={
                            annotatedDraft.length
                          }
                          isLocked={isLocked}
                          onTimeChange={(time) => {
                            changeTaskTime(
                              item.task.id,
                              time,
                            );
                          }}
                          onDurationChange={
                            (durationMinutes) => {
                              changeTaskDuration(
                                item.task.id,
                                durationMinutes,
                              );
                            }
                          }
                          onMove={(direction) => {
                            moveTask(
                              item.task.id,
                              direction,
                            );
                          }}
                          onRemove={() => {
                            removeTask(
                              item.task.id,
                            );
                          }}
                        />
                      ),
                    )}
                  </div>
                ) : (
                  <div className="planning-empty-schedule">
                    <Sparkles size={32} />

                    <strong>
                      День пока свободен
                    </strong>

                    <span>
                      Добавь задачи из списка
                      справа.
                    </span>
                  </div>
                )}
              </main>

              <aside className="planning-side-column">
                <section className="planning-side-card">
                  <div className="planning-side-heading">
                    <Clock3 size={18} />

                    <div>
                      <h3>
                        Свободные интервалы
                      </h3>

                      <span>
                        {freeSlots.length}
                        {" окон"}
                      </span>
                    </div>
                  </div>

                  <div className="planning-free-list">
                    {freeSlots.length > 0 ? (
                      freeSlots.map(
                        (slot) => (
                          <article
                            key={slot.startAt}
                          >
                            <strong>
                              {formatTime(
                                slot.startAt,
                              )}
                              {"–"}
                              {formatTime(
                                slot.endAt,
                              )}
                            </strong>

                            <span>
                              {formatDuration(
                                slot.durationMinutes,
                              )}
                            </span>
                          </article>
                        ),
                      )
                    ) : (
                      <p>
                        Свободных интервалов
                        нет.
                      </p>
                    )}
                  </div>
                </section>

                <section className="planning-side-card">
                  <div className="planning-side-heading">
                    <ListTodo size={18} />

                    <div>
                      <h3>
                        Без времени
                      </h3>

                      <span>
                        {availableTasks.length}
                        {" задач"}
                      </span>
                    </div>
                  </div>

                  <div className="planning-backlog-list">
                    {availableTasks.length
                      > 0 ? (
                      availableTasks.map(
                        (task) => (
                          <BacklogCard
                            key={task.id}
                            task={task}
                            isLocked={isLocked}
                            onAdd={() => {
                              addTask(task);
                            }}
                          />
                        ),
                      )
                    ) : (
                      <p>
                        Все активные задачи
                        уже распределены.
                      </p>
                    )}
                  </div>
                </section>
              </aside>
            </div>
          </>
        ) : (
          <div className="planning-panel-loading">
            <TriangleAlert size={31} />

            <strong>
              План недоступен
            </strong>

            <span>
              Обнови данные ещё раз.
            </span>
          </div>
        )}
      </section>
    </div>
  );
}

export default PlanningPanel;
